import {BehaviorSubject, EMPTY, Observable, Subject} from 'rxjs';
import {FORM_INPUT_FIELDS, ONBOARDING_LAYOUTS_CUSTOMIZATIONS} from '../../../dbc-onboarding/dbc-onboard.utils';
import {
    DBC_FORM_FIELD,
    INDEXDB_DBC_KEYS,
    ONBOARDING_CONTACT_ORDER,
    ONBOARDING_STORE_NAME,
    validateEmail,
    extractUserProperties
} from '../../dbc-onboard-v2.utils';
import {IMAGE_TYPE} from '../../components/dbc-mobile-form/dbc-mobile-form.component';
import {AfterViewInit, Component, HostListener, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {catchError, debounceTime, distinctUntilChanged, filter, finalize, switchMap, takeUntil} from 'rxjs/operators';
import {ActivatedRoute, NavigationEnd, Router} from '@angular/router';
import {DigitalBusinessCard} from '../../../digital-business-card/digital-business-card.model';
import {DbcIndexedDbService} from '../../../global-services/indexed-db.service';
import {SsoAuthService} from '../../../global-services/sso-auth.service';
import {ToastComponent} from '../../../global-components/toast/toast.component';
import {TEMPLATE_LOGO_SIZE_MAP} from '../../../digital-business-card/templates-utils';
import {RecommendationService} from '../../../qr-recommendations/recommendation-service';
import {fadeIn} from '../../dbc-onboarding-v2-animations';
import {BUTTON_STYLES, BUTTON_TYPES} from 'kaizen-design-system';
import {ModalDirective} from 'ngx-bootstrap/modal';
import {parsePhoneNumberString, DASHBOARD_MENU} from '../../../shared/utils';
import { DbcAuth0LinkedInUserDetailsService } from '../../services/dbc-onboard-v2.service';
import { AMPLITUDE_EVENT_CATEGORIES, AMPLITUDE_EVENTS, AmplitudeService } from 'app/global-services/amplitude.service';
import {DashboardService} from '../../../global-services/dashboard.service';
import { AuthService } from 'app/global-services/auth.service';
import { CookieService } from 'ngx-cookie-service';
import {auth0CustomParamsRegex} from '../../../digital-business-card/digital-business-card-utils';
export interface ContactField {
    value: string;
}

enum SCREENS {
    ONBOARDING_FORM,
    SCAN_CARD,
    LINKEDIN_LOADER
}

@Component({
    selector: 'app-dbc-onboard',
    templateUrl: './dbc-onboard.component.html',
    styleUrls: ['./dbc-onboard.component.scss'],
    animations: [fadeIn]
})
export class DbcOnboardComponent implements OnInit, AfterViewInit, OnDestroy {

    // Component variables
    dbc: DigitalBusinessCard;
    isMobile: boolean = false;
    isFirstNameMissing: boolean = false;
    isInvalidEmail: boolean = false;
    defaultColorPickerBackground: string = '#000000';
    showEditImageModal: boolean = false;
    editImageSrc: string;
    editImageTitle: string;
    showSaveLoader: boolean = false;
    showFileSizeErrorModal: boolean = false;
    showSvgErrorModal: boolean = false;
    emailInputErrorMessage: string;

    // phone number variables
    phoneNumber: string;
    fullPhoneNumber: string;
    countryNameCode: string;
    countryCallingCode: string = '1';

    // Subjects
    $resize: Subject<any> = new Subject<void>();
    writeToIndexedDb = new Subject<DigitalBusinessCard>();
    dbcSubject = new BehaviorSubject<any | null>(null);
    dbc$: Observable<any | null> = this.dbcSubject.asObservable();
    ngUnsubscribe: Subject<any> = new Subject<void>();
    faviconSubject = new Subject<string>();
    userEmailParameter: string;
    layoutParameter: string = undefined;
    templateSourceParameter: string;
    isLoading = true;
    paymentFailed: boolean = false;
    isDrawerOpen: boolean = false;
    showCardCreationOptionsModal: boolean = false;
    showLinkedInLoader: boolean = false;

    SCREENS = SCREENS;

    currentScreen = SCREENS.SCAN_CARD;

    showSuccessBottomToast: boolean = false;
    lastSelectedScreen: SCREENS = SCREENS.ONBOARDING_FORM;

    // color picker variables
    colorOptions: string[] = [
        '#3D3D3D',
        '#5E53A3',
        '#0044F5',
        '#14A22E',
        '#EBB400',
        '#CD2B3B',
    ];

    // image variables
    selectedImageType: IMAGE_TYPE;
    selectedImageUrl: string;
    selectedImageFile: File
    isCropperVisible: boolean = false;
    userImageUrl: string = '';
    logoUrl: string = '';
    layoutPrimaryColor: string = '#5E53A3';
    imageToEdit: IMAGE_TYPE;
    getLogoUrlFromEmail: boolean = true;

    // limitExceeded Error modal variables
    fileSizeErrorTitle: string = 'Error';
    fileSizeErrorSummary: string = 'File size exceeds the 50MB limit. Please upload a smaller file.'
    primaryCtaText: string = 'Try Again';
    secondaryCtaText: string = 'Dismiss';
    imageSrc: string = '../../../../assets/img/exclamation-chat.svg';

    // svg error modal variables
    svgErrorTitle: string = 'Error';
    svgErrorSummary: string = 'Unsupported image format. Please upload a JPEG, PNG, or HEIC file.'

    isAuthenticated: boolean = false;

    backgroundImage: string = '../../../assets/img/dbc-onboard/dbc-onboard-bg.png';

    private readonly fieldChangeSubject = new Subject<{field: string; value: string}>();
    private readonly trackedFields = {
        first_name: false,
        company_name: false,
        job_title: false,
        photo: false,
        logo: true,
        email: false,
        website: false,
        background_color: false
    };

    @ViewChild('customToast') customToast!: ToastComponent;
    @ViewChild('upgradeFailModal', {static: true}) upgradeFailModal: ModalDirective;

    constructor(private route: ActivatedRoute,
        private dbcIndexedDbService: DbcIndexedDbService,
        private ssoAuthService: SsoAuthService,
        private router: Router,
        private dbcAuth0LinkedInUserDetailsService: DbcAuth0LinkedInUserDetailsService,
        private readonly dashboardService: DashboardService,
        private readonly recommendationService: RecommendationService,
        private readonly amplitudeService: AmplitudeService,
        private readonly authservice: AuthService,
        private readonly cookieService: CookieService) {
        this.dashboardService.setCurrentDashboard(DASHBOARD_MENU.CARDS);
        this.authservice.setCurrentProduct(DASHBOARD_MENU.CARDS);
        this.fieldChangeSubject.pipe(
            takeUntil(this.ngUnsubscribe),
            debounceTime(500)
        ).subscribe(({field, value}) => {
            const fieldMapping = {
                company: 'company_name',
                designation: 'job_title',
                first_name: 'name',
                photo: 'photo',
                email_v2: 'email',
                website_v2: 'website',
                background_color: 'background_color'
            };

            // Only proceed if the field exists in fieldMapping
            if (field in fieldMapping) {
                const mappedField = fieldMapping[field];

                if (!this.trackedFields[mappedField]) {
                    const eventPayload = {
                        customize_type: mappedField
                    };
                    this.amplitudeService.logEvent(AMPLITUDE_EVENT_CATEGORIES.Engagement, AMPLITUDE_EVENTS.CARD_DETAILS_CUSTOMIZED, eventPayload);
                    this.trackedFields[mappedField] = true;
                }
            }
        });
    }

    @HostListener('window:resize', ['$event'])
    onResize(event) {
        this.$resize.next(event)
    }

    ngOnInit() {
        this.ssoAuthService.isAuthenticatedUser$
            .pipe(filter(res => res !== null), takeUntil(this.ngUnsubscribe))
            .subscribe(res => {
                this.isAuthenticated = res;
            });
        this.initializeDbc();
        this.handleQueryParamSubscription();
        this.checkScreenSize()
        this.loadCountryNameCode();
        this.$resize.pipe(debounceTime(50), takeUntil(this.ngUnsubscribe)).subscribe( _ => {
            this.checkScreenSize();
        })
        this.checkScreenSize()
        this.loadCountryNameCode();
        this.initializeImages()
        this.loadDbcFromIndexedDB();
        this.dbc$.pipe(takeUntil(this.ngUnsubscribe)).subscribe((dbc) => {
            if (dbc) {
                this.dbc = { ...dbc };
            }
        });

        this.router.events.pipe(
            filter(event => event instanceof NavigationEnd)
        ).subscribe(() => {
            this.showSaveLoader = false;
        });

        this.writeToIndexedDb.pipe(
            debounceTime(1000),
            switchMap(dbc => {
                return this.dbcIndexedDbService.setKeyValue(ONBOARDING_STORE_NAME, INDEXDB_DBC_KEYS.DBC, dbc)
            }), takeUntil(this.ngUnsubscribe)
        ).subscribe();

        this.faviconSubject
            .pipe(
                debounceTime(200),
                distinctUntilChanged(),
                switchMap((debouncedDomain) =>
                    this.recommendationService.fetchFaviconWithMinSize(debouncedDomain, 256, 64)
                ), takeUntil(this.ngUnsubscribe)
            )
            .subscribe({
                next: (faviconUrl) => {
                    if (faviconUrl) {
                        this.dbc.logo_url = faviconUrl;
                        this.logoUrl = faviconUrl;
                        this.dbc.logo_size = TEMPLATE_LOGO_SIZE_MAP[Number(this.dbc.layout)];
                        this.dbcIndexedDbService
                            .deleteKey(ONBOARDING_STORE_NAME, INDEXDB_DBC_KEYS.LOGO_URL)
                            .subscribe();
                    } else {
                        this.dbc.logo_url = '';
                        this.logoUrl = '';
                    }
                },
                error: (error) => {
                    console.error('Error fetching favicon:', error);
                },
            });
        this.sendIntercomCookieDataToAmplitude();
    }

    sendIntercomCookieDataToAmplitude() {
        try {
            const intercomUserAttribute = this.cookieService.get('intercom_user_attribute');
            if (!intercomUserAttribute) {
                return;
            }

            const intercom_user_attribute = JSON.parse(intercomUserAttribute);
            const user_properties = extractUserProperties(intercom_user_attribute);

            if (Object.keys(user_properties).length > 0) {
                this.amplitudeService.setUserProperties(user_properties);
            }

        } catch (error) {
            console.error('Error setting intercom cookie data to amplitude:', error);
        }
    }

    initializeImages() {
        this.dbcIndexedDbService.getKeyValue(ONBOARDING_STORE_NAME, INDEXDB_DBC_KEYS.USER_IMAGE_URL).pipe(takeUntil(this.ngUnsubscribe)).subscribe((userImageUrl) => {
            this.userImageUrl = userImageUrl;
        });

        this.dbcIndexedDbService.getKeyValue(ONBOARDING_STORE_NAME, INDEXDB_DBC_KEYS.LOGO_URL)
            .pipe(takeUntil(this.ngUnsubscribe))
            .subscribe((logoUrl) => {
                if (logoUrl) {
                    this.logoUrl = logoUrl;
                } else {
                    // Check the 'dbc' object if logoUrl is not found in logo_url key
                    this.dbcIndexedDbService.getKeyValue(ONBOARDING_STORE_NAME, 'dbc')
                        .pipe(takeUntil(this.ngUnsubscribe))
                        .subscribe((dbcObj) => {
                            if (dbcObj?.logo_url) {
                                this.logoUrl = dbcObj.logo_url;
                            } else {
                                this.logoUrl = '';
                            }
                        });
                }
            });

    }

    loadDbcFromIndexedDB(): void {
        this.dbcIndexedDbService
            .getKeyValue(ONBOARDING_STORE_NAME, INDEXDB_DBC_KEYS.DBC).pipe(takeUntil(this.ngUnsubscribe)).subscribe((dbc) => {
                this.layoutPrimaryColor = dbc?.customizations?.background_color || this.layoutPrimaryColor;
                this.dbcSubject.next(dbc);
            });
    }

    handleQueryParamSubscription() {
        // Fallback: Ensure isLoading is set to false after 10 seconds
        const fallbackTimeout = setTimeout(() => {
            this.isLoading = false;
            console.warn('Fallback: isLoading set to false after 10 seconds');
        }, 10000);

        this.route.queryParamMap.pipe(
            takeUntil(this.ngUnsubscribe),
            catchError((err) => {
                console.error('Error fetching query params:', err);
                return EMPTY; // Ensures the stream doesn't break
            })
        ).subscribe({
            next: (paramsMap) => {
                if (paramsMap.has('email')) {
                    this.userEmailParameter = paramsMap.get('email');
                    this.dbc.email_v2 = [{label: '', value: this.userEmailParameter, valid: true}];
                }

                if (paramsMap.has('full_name')) {
                    this.dbc.first_name = paramsMap.get('full_name');
                }

                if (paramsMap.has('layout')) {
                    this.layoutParameter = paramsMap.get('layout') || '1';
                }

                if (paramsMap.has('template_source')) {
                    this.templateSourceParameter = paramsMap.get('template_source');
                }

                if (paramsMap.has('flow') && paramsMap.get('flow') === 'linkedin') {
                    this.setAuth0LinkedInUserDetails();
                }

                if (paramsMap.has('dbc_data')) {
                    try {
                        const dbcDataReceivedFromParamParsed = JSON.parse(atob(paramsMap.get('dbc_data')));
                        if (this.templateSourceParameter === 'qrcg' || this.templateSourceParameter === 'post-scan') {
                            this.dbc = dbcDataReceivedFromParamParsed
                                ? new DigitalBusinessCard(dbcDataReceivedFromParamParsed)
                                : new DigitalBusinessCard();
                        } else {
                            this.layoutParameter = dbcDataReceivedFromParamParsed?.layout || this.layoutParameter;
                        }

                        // @ts-ignore
                        if (!this.dbc.email_v2?.length || (!this.dbc.email_v2[0]?.value)) {
                            this.dbc.email_v2 = this.userEmailParameter
                                ? [{label: 'work', value: this.userEmailParameter, valid: true}]
                                : [];
                        }

                        this.dbc.layout = this.layoutParameter || '1';

                        this.dbcIndexedDbService.setKeyValue(ONBOARDING_STORE_NAME, INDEXDB_DBC_KEYS.DBC, this.dbc)
                            .subscribe(() => {
                                if (this.templateSourceParameter === 'qrcg') {
                                    this.onSaveCard();
                                } else {
                                    this.isLoading = false;
                                    clearTimeout(fallbackTimeout);
                                }
                            });
                    } catch (error) {
                        console.error('Error parsing dbc_data:', error);
                        this.isLoading = false;
                        clearTimeout(fallbackTimeout);
                    }
                } else {
                    this.loadDbcFromIndexedDB();
                    this.isLoading = false;
                    clearTimeout(fallbackTimeout);
                }

                if (paramsMap.has('payment')) {
                    this.paymentFailed = paramsMap.get('payment') === 'failed';
                    if (this.paymentFailed) {
                        this.showUpgradeFailedModal();
                    }
                }
            },
            error: (err) => {
                this.isLoading = false;
                clearTimeout(fallbackTimeout);
                console.error('Error in query param subscription:', err);
            }
        })

        // Added fragment subscription to navigate to the correct screen
        this.route.fragment.pipe(takeUntil(this.ngUnsubscribe)).subscribe({
            next: (fragment) => {
                if (fragment === 'scan-card') {
                    this.currentScreen = SCREENS.SCAN_CARD;
                } else if (fragment === 'linkedin-loader') {
                    this.currentScreen = SCREENS.LINKEDIN_LOADER;
                } else {
                    this.currentScreen = SCREENS.ONBOARDING_FORM;
                }
            }
        })
    }

    showCardCreationOptions() {
        this.showCardCreationOptionsModal = true;
    }

    ngAfterViewInit() {
        if (this.isMobile && !this.paymentFailed) {
            this.isDrawerOpen = true;
        } else if (!this.isMobile) {
            this.showModalDrawer();
        }

        this.isLoading = false;
    }

    setEmailInputErrorMessage(value: string) {
        if (!value.trim()) {
            this.emailInputErrorMessage = 'Required'
        } else {
            this.emailInputErrorMessage = 'Email is not valid'
        }
    }

    signUpWithLinkedIn() {
        if (this.isMobile) {
            this.isDrawerOpen = false;
            this.switchScreen(SCREENS.LINKEDIN_LOADER);
        } else {
            this.showLinkedInLoader = true;
        }
        this.lastSelectedScreen = SCREENS.LINKEDIN_LOADER;
        this.logAmplitudeEvent(this.lastSelectedScreen, AMPLITUDE_EVENT_CATEGORIES.Onboarding, AMPLITUDE_EVENTS.CARD_CREATION_METHOD_SELECTED);
        this.ssoAuthService.login('/digital-business-cards/onboarding?flow=linkedin', {
            signup: true,
            plan: 'SFT-DBC-FREE-YEAR',
            flow: 'linkedin',
        });
    }

    setAuth0LinkedInUserDetails() {
        this.dbcAuth0LinkedInUserDetailsService.getAuth0LinkedInUserDetails().subscribe(
            (res) => {
                if (res && res['exists'] === true) {
                    this.dbc.first_name = res['name'] || '';
                    this.dbc.user_image_url = res['picture'] || '';
                    this.userImageUrl = res['picture'] || '';
                    this.onInputValueChange({ value: res['email'], type: DBC_FORM_FIELD.EMAIL }, false);
                    this.lastSelectedScreen = SCREENS.LINKEDIN_LOADER;
                    this.linkedAmplitudeEvent('successful');
                    this.showCustomToast('success', 'LinkedIn user details fetched successfully');
                    this.closeDrawerOrOptionsModal();
                    this.logAmplitudeEvent(this.lastSelectedScreen, AMPLITUDE_EVENT_CATEGORIES.Onboarding, AMPLITUDE_EVENTS.BUILD_YOUR_CARD);
                } else {
                    this.showCustomToast('danger', 'Error fetching LinkedIn user details');
                    this.linkedAmplitudeEvent('failed');
                }
            },
            (err) => {
                this.showCustomToast('danger', 'Error fetching LinkedIn user details');
                console.error('Error fetching Auth0 LinkedIn user details:', err);
            }
        );
    }

    linkedAmplitudeEvent(status: string){
        this.amplitudeService.logEvent(AMPLITUDE_EVENT_CATEGORIES.Onboarding, AMPLITUDE_EVENTS.LINKEDIN_PROFILE_PROCESSED, {
            status: status
        });
    }

    showModalDrawer() {
        this.showCardCreationOptions();
    }

    initializeDbc() {
        const email_v2 = [
            {
                value: '',
                label: '',
                valid: true,
            },
        ];

        const phone_v2 = [
            {
                value: '',
                label: '',
                valid: true,
            },
        ];

        const website_v2 = [
            {
                value: '',
                label: '',
                valid: true,
            },
        ];

        const productData = {
            layout: '1',
            first_name: '',
            last_name: '', // Added last_name
            designation: '',
            company: '',
            email_v2,
            phone_v2,
            website_v2,
            __onboarding_layout__: true,
            __zoom__: 0.7,
            customizations: {
                ...ONBOARDING_LAYOUTS_CUSTOMIZATIONS['1'],
                background_color: '#5E53A3',
                icon_color: '#5E53A3',
                button_color: '#5E53A3'
            },
            contact_info_ordering: ONBOARDING_CONTACT_ORDER
        };

        const dbc =  this.cleanUpInitialData(new DigitalBusinessCard(productData));
        this.fixDbcDefaultValues(dbc)
    }

    cleanUpInitialData(productData: DigitalBusinessCard) {
        productData.customizations.typography.contact_details.google_font_size = 16;
        productData.customizations.typography.contact_details.google_font_style = 'Regular';
        return new DigitalBusinessCard(productData);
    }

    setDefault(value: string | undefined, defaultValue: string): string {
        return value || defaultValue;
    }

    fixDbcDefaultValues(dbc: any) {
        if (dbc.email_v2 && dbc.email_v2.length > 0) {
            dbc.email_v2[0].value = this.setDefault(dbc.email_v2[0].value, '');
        }

        if (dbc.phone_v2 && dbc.phone_v2.length > 0) {
            dbc.phone_v2[0].value = this.setDefault(dbc.phone_v2[0].value, '');
        }

        if (dbc.website_v2 && dbc.website_v2.length > 0) {
            dbc.website_v2[0].value = this.setDefault(dbc.website_v2[0].value, '');
        }

        if (dbc.first_name === 'John') {
            dbc.first_name = '';
        }

        if (!dbc.designation) {
            dbc.designation = '';
        }

        if (!dbc.company) {
            dbc.company = '';
        }

        this.dbc = dbc;
    }

    loadCountryNameCode(): void {
        this.dbcIndexedDbService.getKeyValue(ONBOARDING_STORE_NAME, INDEXDB_DBC_KEYS.COUNTRY_NAME_CODE).pipe(takeUntil(this.ngUnsubscribe)).subscribe((countryNameCode) => {
            if (countryNameCode !== null) {
                this.countryNameCode = countryNameCode;
            } else {
                this.countryNameCode = 'US';
            }

            // Now, update the phone number details based on the country
            const phone = this.dbc?.phone_v2[0] as ContactField;
            if (phone?.value) {
                this.fullPhoneNumber = phone.value.replace(/^\+/, '');
                this.phoneNumber = this.fullPhoneNumber.split(' ')[1];
                this.countryCallingCode = this.fullPhoneNumber.split(' ')[0];
            }
        });

    }

    onInputValueChange({ value, type }: { value: string, type: DBC_FORM_FIELD }, manualInputChange: boolean = true): void {
        switch (type) {
            case DBC_FORM_FIELD.FIRST_NAME:
                this.dbc.first_name = value
                this.isFirstNameMissing = false;
                break;

            case DBC_FORM_FIELD.DESIGNATION:
                this.dbc.designation = value;
                break;

            case DBC_FORM_FIELD.COMPANY:
                this.dbc.company = value;
                break;

            case DBC_FORM_FIELD.PHONE_NUMBER:
                this.phoneNumber = value
                if (value !== '') {
                    this.attachCallingCode();
                } else {
                    this.fullPhoneNumber = '';
                }
                this.dbc.phone_v2 = [{ valid: true, value: this.fullPhoneNumber, label: '' }];
                break;

            case DBC_FORM_FIELD.EMAIL:
                this.dbc.email_v2 = [{ valid: true, value, label: '' }];
                this.isInvalidEmail = !validateEmail(value) && value !== '';
                this.setEmailInputErrorMessage(value)
                if (!this.isInvalidEmail && this.getLogoUrlFromEmail) {
                    const domain = value.split('@')[1];
                    if (domain) {
                        this.faviconSubject.next(domain);
                    }
                }
                break;
            case DBC_FORM_FIELD.WEBSITE:
                this.dbc.website_v2 = [{ valid: true, value, label: '' }];
                break;

            case DBC_FORM_FIELD.BACKGROUND_COLOR:
                this.dbc.customizations.background_color = value;
                this.dbc.customizations.icon_color = value;
                this.dbc.customizations.button_color = value;
                this.layoutPrimaryColor = value;
                break;
            default:
                break;
        }

        this.dbcSubject.next(this.dbc);
        this.writeToIndexedDb.next(this.dbc);
        if (manualInputChange){
            this.fieldChangeSubject.next({field : type, value});
        }
    }

    attachCallingCode() {
        this.fullPhoneNumber = `+${this.countryCallingCode} ${this.phoneNumber}`;
    }

    onCountryChange(event) {
        this.dbcIndexedDbService.setKeyValue(ONBOARDING_STORE_NAME, INDEXDB_DBC_KEYS.COUNTRY_NAME_CODE, event.countryNameCode).subscribe(() => {
            this.countryNameCode = event.countryNameCode;
        });
        this.countryCallingCode = event.countryCallingCode
        if (this.phoneNumber) {
            this.onInputValueChange({value: this.phoneNumber, type: DBC_FORM_FIELD.PHONE_NUMBER});
        } else if (this.phoneNumber === 'undefined') {
            this.onInputValueChange({value: '', type: DBC_FORM_FIELD.PHONE_NUMBER});
        }
    }

    handleImageUpload(event: { type: IMAGE_TYPE, file: File }) {
        this.selectedImageType = event.type;
        const fileSizeInMB = event.file.size / (1024 * 1024);

        // Check if the file is SVG
        if (event.file.type === 'image/svg+xml') {
            this.showSvgErrorModal = true;
            return;
        }

        // Check if the file size exceeds 50MB
        if (event.file && fileSizeInMB > 50) {
            this.showFileSizeErrorModal = true;
            return;
        }

        if (event.file) {
            const reader = new FileReader();

            reader.onload = () => {
                this.selectedImageUrl = reader.result as string;
                this.selectedImageFile = event.file;
                this.isCropperVisible = true;
            };
            reader.readAsDataURL(event.file);
            const customizeType = event.type === IMAGE_TYPE.PROFILE ? 'photo' : 'logo';
            if (!this.trackedFields[customizeType]) {
                const eventPayload = {
                    customize_type: customizeType
                };
                this.amplitudeService.logEvent(AMPLITUDE_EVENT_CATEGORIES.Engagement, AMPLITUDE_EVENTS.CARD_DETAILS_CUSTOMIZED, eventPayload);
                this.trackedFields[customizeType] = true;
            }
        }
    }

    onCroppedImage(croppedFile: File) {
        const reader = new FileReader();
        reader.readAsDataURL(croppedFile);
        reader.onload = () => {
            const croppedImageUrl = reader.result as string;
            if (this.selectedImageType === IMAGE_TYPE.PROFILE) {
                this.userImageUrl = croppedImageUrl;
                this.dbc.user_image_url = croppedImageUrl
                this.editImageSrc = croppedImageUrl
                this.dbcIndexedDbService.setKeyValue(ONBOARDING_STORE_NAME, INDEXDB_DBC_KEYS.USER_IMAGE_URL, this.userImageUrl).subscribe();
            } else if (this.selectedImageType === IMAGE_TYPE.LOGO) {
                this.logoUrl = croppedImageUrl;
                this.dbc.logo_url = croppedImageUrl
                this.editImageSrc = croppedImageUrl
                this.getLogoUrlFromEmail = false;
                this.dbcIndexedDbService.setKeyValue(ONBOARDING_STORE_NAME, INDEXDB_DBC_KEYS.LOGO_URL, this.logoUrl).subscribe();
            }
            this.isCropperVisible = false;
            this.writeToIndexedDb.next(this.dbc);
            this.dbcSubject.next(this.dbc);
        };
    }

    closeCropper() {
        this.isCropperVisible = false;
    }

    checkScreenSize() {
        this.isMobile = window.innerWidth < 768;
    }

    closeDrawerOrOptionsModal() {
        if (this.isMobile){
            this.isDrawerOpen = false;
        } else{
            this.showCardCreationOptionsModal = false;
        }
    }

    switchScreen(event: SCREENS) {
        if (this.isDrawerOpen) {
            this.closeDrawerOrOptionsModal();
        }

        // Add fragments to url without redirection in case of back button
        // use angular router to navigate to the same page with query params
        switch (event) {
            case SCREENS.ONBOARDING_FORM:
                this.router.navigate(['/digital-business-cards/onboarding'], {
                    fragment: '',
                    skipLocationChange: false,
                    relativeTo: this.route
                });
                break;
            case SCREENS.SCAN_CARD:
                this.router.navigate(['/digital-business-cards/onboarding'], {
                    fragment: 'scan-card',
                    skipLocationChange: false,
                    relativeTo: this.route
                });
                break;
            case SCREENS.LINKEDIN_LOADER:
                this.router.navigate(['/digital-business-cards/onboarding'], {
                    fragment: 'linkedin-loader',
                    skipLocationChange: false,
                    relativeTo: this.route
                });
                break;

        }
    }

    onSummarizeDataReceive(event) {
        const emailArray = event.email ? [{label: 'work', value: event.email, valid: true}] : [];
        const phoneArray = event.phone ? [{label: 'work', value: event.phone, valid: true}] : [];
        const websiteArray = event.website ? [{label: 'work', value: event.website, valid: true}] : [];

        try {
            event['first_name'] = event['first_name'] + (event['last_name'] ? ' ' + event['last_name'] : '');
            event['last_name'] = '';
        } catch {
            console.error('Error in parsing name');
        }

        const data = {
            ...event,
            email_v2: emailArray,
            phone_v2: phoneArray,
            website_v2: websiteArray
        }
        this.dbc = {...this.dbc, ...data};
        this.parsePhoneNumber();
        this.writeToIndexedDb.next(this.dbc);
        this.switchScreen(SCREENS.ONBOARDING_FORM);
        this.logAmplitudeEvent(this.lastSelectedScreen, AMPLITUDE_EVENT_CATEGORIES.Onboarding, AMPLITUDE_EVENTS.BUILD_YOUR_CARD);
        this.showScanCardSuccessToast();
    }

    parsePhoneNumber() {
        const phone = this.dbc?.phone_v2[0] as ContactField;
        if (phone?.value) {
            const parsed = parsePhoneNumberString(phone.value);

            this.fullPhoneNumber = parsed.formatted; // e.g. "+1 213 373 4253"
            this.phoneNumber = parsed.nationalNumber; // e.g. "2133734253"
            this.countryCallingCode = parsed.countryCallingCode ? parsed.countryCallingCode : '1'; // e.g. "1"
            this.countryNameCode = parsed.country ? parsed.country : 'US'; // e.g. "US"

            // @ts-ignore
            this.dbc.phone_v2[0].value =  this.fullPhoneNumber;
        }
        this.onCountryChange({countryCallingCode: this.countryCallingCode, countryNameCode: this.countryNameCode});
    }

    onScanError() {
        this.showCustomToast('danger', 'Failed to process image data.')
    }

    goToEnterDetails(){
        this.closeDrawerOrOptionsModal();
        this.lastSelectedScreen = SCREENS.ONBOARDING_FORM;
        this.logAmplitudeEvent(this.lastSelectedScreen, AMPLITUDE_EVENT_CATEGORIES.Onboarding, AMPLITUDE_EVENTS.CARD_CREATION_METHOD_SELECTED);
        this.logAmplitudeEvent(this.lastSelectedScreen, AMPLITUDE_EVENT_CATEGORIES.Onboarding, AMPLITUDE_EVENTS.BUILD_YOUR_CARD);
    }

    goToScanCard(){
        this.closeDrawerOrOptionsModal();
        this.lastSelectedScreen = SCREENS.SCAN_CARD;
        this.logAmplitudeEvent(this.lastSelectedScreen, AMPLITUDE_EVENT_CATEGORIES.Onboarding, AMPLITUDE_EVENTS.CARD_CREATION_METHOD_SELECTED);
        this.switchScreen(SCREENS.SCAN_CARD);
    }

    logAmplitudeEvent(screen: SCREENS, category: AMPLITUDE_EVENT_CATEGORIES, event: AMPLITUDE_EVENTS){
        switch (screen) {
            case SCREENS.ONBOARDING_FORM:
                this.amplitudeService.logEvent(
                    category, event,
                    {
                        card_creation_method: 'enter_details'
                    }
                );
                break;
            case SCREENS.SCAN_CARD:
                this.amplitudeService.logEvent(
                    category, event,
                    {
                        card_creation_method: 'scan_my_card'
                    }
                );
                break;
            case SCREENS.LINKEDIN_LOADER:
                this.amplitudeService.logEvent(
                    category, event,
                    {
                        card_creation_method: 'continue_with_linkedin'
                    }
                );
                break;
        }
    }

    showCustomToast(type: 'success' | 'danger' = 'danger', message: string = 'Failed to perform operation') {
        this.customToast.type = type;
        this.customToast.message = message;
        this.customToast.showToast(3000);
    }

    showScanCardSuccessToast() {
        if (this.isMobile) {
            this.showSuccessBottomToast = true;
            setTimeout(() => {
                this.showSuccessBottomToast = false;
            }, 3000)
        } else {
            this.showCustomToast('success', 'Image processed successfully.');
        }
    }

    onSaveCard() {
        this.isFirstNameMissing = !this.dbc?.first_name;

        const email = this.dbc?.email_v2?.[0] as ContactField;
        this.isInvalidEmail = !validateEmail(email?.value) || !email?.value?.trim();

        if (this.isInvalidEmail) {
            this.setEmailInputErrorMessage(email?.value);
        }
        if (this.isFirstNameMissing || this.isInvalidEmail) {
            this.setInputErrorState();
        }

        this.dbc.layout = this.layoutParameter || '1'; // Giving preference to layoutParameter

        if (this.dbc?.first_name && !this.isInvalidEmail) {
            this.showSaveLoader = true;
            this.dbcIndexedDbService.setKeyValue(ONBOARDING_STORE_NAME, INDEXDB_DBC_KEYS.DBC, this.dbc).pipe(finalize(() => {
                if (this.isAuthenticated) {
                    this.router.navigate(['digital-business-cards/onboarding/post-signup']);
                    this.isLoading = false;
                } else {
                    const username = this.dbc?.first_name || '';
                    const extParams: any = {
                        signup: true,
                        plan: 'SFT-DBC-FREE-YEAR',
                        email: this.userEmailParameter || email.value,
                    };

                    if (auth0CustomParamsRegex.test(username)) {
                        extParams.ext_username = username;
                    }

                    this.ssoAuthService.login('/digital-business-cards/onboarding/post-signup', extParams);
                    this.isLoading = false;
                }
            }), takeUntil(this.ngUnsubscribe)).subscribe()
            this.logAmplitudeEvent(this.lastSelectedScreen, AMPLITUDE_EVENT_CATEGORIES.Onboarding, AMPLITUDE_EVENTS.SAVE_MY_CARD);
        } else {
            this.isLoading = false;
        }
    }

    setInputErrorState() {
        const emailInput = document.getElementById('email_v2');
        const nameInput = document.getElementById('first_name')

        if (nameInput && this.isFirstNameMissing) {
            nameInput.focus()
            nameInput.scrollIntoView({ behavior: 'smooth', block: 'center' });
        } else if (emailInput && this.isInvalidEmail) {
            emailInput.focus()
            emailInput.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
    }

    handleOpenEditImageModal(type: IMAGE_TYPE) {
        this.showEditImageModal = true;
        if (type === IMAGE_TYPE.PROFILE) {
            this.editImageSrc = this.userImageUrl;
            this.imageToEdit = IMAGE_TYPE.PROFILE;
            this.editImageTitle = 'Profile Photo'
        } else if (type === IMAGE_TYPE.LOGO) {
            this.editImageSrc = this.logoUrl;
            this.imageToEdit = IMAGE_TYPE.LOGO
            this.editImageTitle = 'Logo';
        }
    }

    onRemovePhoto() {
        if (this.imageToEdit === IMAGE_TYPE.LOGO) {
            this.dbc.logo_url = ''
            this.logoUrl = ''
            this.editImageSrc = ''
            this.dbcIndexedDbService.deleteKey(ONBOARDING_STORE_NAME, INDEXDB_DBC_KEYS.LOGO_URL).pipe(takeUntil(this.ngUnsubscribe)).subscribe();
        } else if (this.imageToEdit === IMAGE_TYPE.PROFILE) {
            this.dbc.user_image_url = ''
            this.userImageUrl = ''
            this.editImageSrc = ''
            this.dbcIndexedDbService.deleteKey(ONBOARDING_STORE_NAME, INDEXDB_DBC_KEYS.USER_IMAGE_URL).pipe(takeUntil(this.ngUnsubscribe)).subscribe();
        }
        this.showEditImageModal = false;
        this.dbcSubject.next(this.dbc);
        this.writeToIndexedDb.next(this.dbc);
    }

    onCloseEditPhotoModal() {
        this.showEditImageModal = false;
    }

    onChangePhoto() {
        let fileInput: HTMLInputElement;
        if (this.imageToEdit === IMAGE_TYPE.PROFILE) {
            fileInput = document.getElementById('file-input-profile') as HTMLInputElement;
        } else {
            fileInput = document.getElementById('file-input-logo') as HTMLInputElement;
        }
        if (fileInput) {
            fileInput.click();
        }
    }

    handleErrorModalPrimaryCtaClick() {
        this.showFileSizeErrorModal = false;
        this.showSvgErrorModal = false;
        let fileInput: HTMLInputElement;
        if (this.selectedImageType === IMAGE_TYPE.PROFILE) {
            fileInput = document.getElementById('file-input-profile') as HTMLInputElement;
        } else {
            fileInput = document.getElementById('file-input-logo') as HTMLInputElement;
        }
        if (fileInput) {
            fileInput.click();
        }
    }

    handleErrorModalSecondaryCtaClick() {
        this.showFileSizeErrorModal = false;
        this.showSvgErrorModal = false;
    }

    onRetryPaymentClick() {
        window.open(
            '/account/upgrade?source=direct-purchase&plan=team&user_seats=1',
            '_blank',
            'noopener noreferrer',
        );
    }
    showUpgradeFailedModal() {
        this.upgradeFailModal.show();
    }

    closeUpgradeFailedModal() {
        this.upgradeFailModal.hide();
        this.paymentFailed = false;
        if (this.isMobile) {
            this.isDrawerOpen = true;
        }
    }

    ngOnDestroy() {
        this.showSaveLoader = false;
        this.ngUnsubscribe.next(null);
        this.ngUnsubscribe.complete();
    }

    protected readonly FORM_INPUT_FIELDS = FORM_INPUT_FIELDS;
    protected readonly BUTTON_TYPES = BUTTON_TYPES;
    protected readonly BUTTON_STYLES = BUTTON_STYLES;
}
